import { type AxiosError } from 'axios';
import { sha256 } from 'js-sha256';

import { getAuthHeader } from '@/entities/auth';
import { Api } from '@/shared/api/types/schema';
import { env } from '@/shared/lib/config/env';

import {
  AUTH_BLOCK_CODE_DEACTIVATED,
  clearAuthBlockState,
  getLocalStorageItem,
  removeLocalStorageItem,
  setAuthBlockState,
  setLocalStorageItem,
} from '../../utils';

/** Глобальный флаг: только один 401 может инициировать редирект на страницу логина */
let isRedirectingToAuth = false;
const CURRENT_COMPANY_STORAGE_KEY = 'protocolist-current-company-id';
const SKIP_COMPANY_ID_HEADER = 'X-Skip-Company-Id';

const hasExplicitCompanyIdParam = (params: unknown): boolean => {
  if (!params) return false;

  if (params instanceof URLSearchParams) {
    return params.has('company_id');
  }

  if (typeof params === 'object') {
    return Object.prototype.hasOwnProperty.call(params, 'company_id');
  }

  return false;
};

const shouldSkipCompanyIdInjection = (headers: unknown): boolean => {
  if (!headers) return false;

  if (typeof headers === 'object' && headers !== null && 'get' in headers) {
    const value = (headers as { get: (name: string) => string | undefined }).get(
      SKIP_COMPANY_ID_HEADER,
    );
    return value === '1';
  }

  if (typeof headers === 'object' && headers !== null) {
    const headersRecord = headers as Record<string, unknown>;
    return (
      headersRecord[SKIP_COMPANY_ID_HEADER] === '1' ||
      headersRecord[SKIP_COMPANY_ID_HEADER.toLowerCase()] === '1'
    );
  }

  return false;
};

const removeSkipCompanyIdHeader = (headers: unknown): void => {
  if (!headers) return;

  if (typeof headers === 'object' && headers !== null && 'delete' in headers) {
    (headers as { delete: (name: string) => void }).delete(SKIP_COMPANY_ID_HEADER);
    (headers as { delete: (name: string) => void }).delete(SKIP_COMPANY_ID_HEADER.toLowerCase());
    return;
  }

  if (typeof headers === 'object' && headers !== null) {
    const headersRecord = headers as Record<string, unknown>;
    delete headersRecord[SKIP_COMPANY_ID_HEADER];
    delete headersRecord[SKIP_COMPANY_ID_HEADER.toLowerCase()];
  }
};

type OptionalCompanyRequestConfig = {
  headers?: Record<string, string>;
  params?: Record<string, unknown> | URLSearchParams;
};

export const withOptionalCompanyId = <T extends OptionalCompanyRequestConfig>(
  companyId?: string | null,
  config?: T,
): T => {
  const nextConfig = { ...(config ?? {}) } as T;

  if (companyId) {
    if (nextConfig.params instanceof URLSearchParams) {
      nextConfig.params = new URLSearchParams(nextConfig.params) as T['params'];
      (nextConfig.params as URLSearchParams).set('company_id', companyId);
    } else {
      nextConfig.params = {
        ...(typeof nextConfig.params === 'object' && nextConfig.params !== null
          ? nextConfig.params
          : {}),
        company_id: companyId,
      } as T['params'];
    }

    return nextConfig;
  }

  nextConfig.headers = {
    ...(nextConfig.headers ?? {}),
    [SKIP_COMPANY_ID_HEADER]: '1',
  };

  return nextConfig;
};

const api = new Api({
  baseURL: env.VITE_API_URL,
  securityWorker() {
    return {
      headers: {
        ...getAuthHeader(),
      },
    };
  },
});

const getBackendErrorCode = (payload: unknown): string | null => {
  if (!payload || typeof payload !== 'object') return null;

  const record = payload as {
    error?: { code?: string; message?: string } | string;
    detail?: { code?: string; message?: string } | string;
  };

  if (record.error && typeof record.error === 'object' && record.error.code) {
    return record.error.code;
  }

  if (record.detail && typeof record.detail === 'object' && record.detail.code) {
    return record.detail.code;
  }

  return null;
};

const getBackendErrorMessage = (payload: unknown): string => {
  if (!payload || typeof payload !== 'object') {
    return 'Ваш аккаунт деактивирован';
  }

  const record = payload as {
    error?: { code?: string; message?: string } | string;
    detail?: { code?: string; message?: string } | string;
  };

  if (record.error && typeof record.error === 'object' && record.error.message) {
    return record.error.message;
  }

  if (record.detail && typeof record.detail === 'object' && record.detail.message) {
    return record.detail.message;
  }

  return 'Ваш аккаунт деактивирован';
};

api.instance.interceptors.request.use(
  (config) => {
    const token: string | null = getLocalStorageItem('token');
    const companyId = getLocalStorageItem(CURRENT_COMPANY_STORAGE_KEY) || null;
    const skipCompanyIdInjection = shouldSkipCompanyIdInjection(config.headers);

    removeSkipCompanyIdHeader(config.headers);

    if (token) {
      // Используем метод set для работы с AxiosHeaders
      config.headers?.set('Authorization', `Bearer ${token}`);
    }

    if (!skipCompanyIdInjection && companyId && !hasExplicitCompanyIdParam(config.params)) {
      if (config.params instanceof URLSearchParams) {
        config.params.set('company_id', companyId);
      } else if (typeof config.params === 'object' && config.params !== null) {
        config.params = {
          ...config.params,
          company_id: companyId,
        };
      } else {
        config.params = { company_id: companyId };
      }
    }

    return config;
  },
  (error) => Promise.reject(error),
);

const getCurrentAppUrl = (): string => window.location.origin + window.location.pathname;

const trimTrailingSlashes = (value: string): string => value.replace(/\/+$/, '');

/** URL для редиректа после логина — без query/hash, чтобы не тащить старый token в redirect-параметр */
export const getRedirectUrl = (): string => encodeURIComponent(getCurrentAppUrl());

const getAuthProvider = (): '3d' | 'keycloak' | '3d-only-query' => {
  return (env.VITE_AUTH_PROVIDER || '3d') as '3d' | 'keycloak' | '3d-only-query';
};

// PKCE helper functions
function generateCodeVerifier(): string {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  const base64 = btoa(String.fromCharCode(...array));
  return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
}

// Проверка доступности crypto.subtle (доступен только в безопасном контексте)
function isCryptoSubtleAvailable(): boolean {
  return (
    typeof crypto !== 'undefined' &&
    typeof crypto.subtle !== 'undefined' &&
    typeof crypto.subtle.digest === 'function'
  );
}

// Конвертация hex строки в base64url
function hexToBase64Url(hex: string): string {
  // Конвертируем hex в Uint8Array
  const bytes = new Uint8Array(hex.match(/.{1,2}/g)!.map((byte) => parseInt(byte, 16)));
  // Конвертируем в base64
  const base64 = btoa(String.fromCharCode(...bytes));
  // Конвертируем в base64url (заменяем + на -, / на _, убираем =)
  return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
}

async function generateCodeChallenge(verifier: string): Promise<string> {
  // Всегда используем S256 для совместимости с Keycloak
  try {
    // Проверяем доступность crypto.subtle
    if (isCryptoSubtleAvailable()) {
      // Используем crypto.subtle если доступен (HTTPS)
      const encoder = new TextEncoder();
      const data = encoder.encode(verifier);
      const digest = await crypto.subtle.digest('SHA-256', data);
      const base64 = btoa(String.fromCharCode(...new Uint8Array(digest)));
      return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
    } else {
      // Используем js-sha256 библиотеку для генерации SHA-256 хеша (HTTP)
      // Это позволяет использовать S256 метод даже без crypto.subtle
      const hashHex = sha256(verifier);
      return hexToBase64Url(hashHex);
    }
  } catch (error) {
    console.error('Ошибка при генерации code challenge:', error);
    throw new Error('Не удалось сгенерировать code challenge для PKCE');
  }
}

const getKeycloakAuthUrl = async (): Promise<string> => {
  const keycloakUrl = env.VITE_KEYCLOAK_URL;
  const realm = env.VITE_KEYCLOAK_REALM;
  const clientId = env.VITE_KEYCLOAK_CLIENT_ID;
  const apiUrl = env.VITE_API_URL;

  // Generate PKCE parameters
  const codeVerifier = generateCodeVerifier();
  const codeChallenge = await generateCodeChallenge(codeVerifier);

  // Всегда используем S256 метод (теперь поддерживается даже без crypto.subtle)
  const codeChallengeMethod = 'S256';

  // Store code_verifier in localStorage as backup
  setLocalStorageItem('pkce_code_verifier', codeVerifier);

  // Redirect URI should point to backend callback endpoint (without query params)
  const cleanApiUrl = trimTrailingSlashes(apiUrl);
  const redirectUri = `${cleanApiUrl}/auth/keycloak/callback`;

  // Return URL без query/hash — после callback бэкенд добавит только ?token=..., иначе получится ?token=старый&token=новый и в localStorage попадёт старый
  const returnUrl = getCurrentAppUrl();
  const stateData = {
    url: returnUrl,
    verifier: codeVerifier,
    clientId: clientId, // Store which client was used for authorization
  };
  const state = encodeURIComponent(btoa(JSON.stringify(stateData)));

  return `${keycloakUrl}/realms/${realm}/protocol/openid-connect/auth?client_id=${clientId}&redirect_uri=${encodeURIComponent(redirectUri)}&response_type=code&scope=openid%20email&state=${state}&code_challenge=${codeChallenge}&code_challenge_method=${codeChallengeMethod}`;
};

export const getAuthUrl = async (): Promise<string> => {
  const provider = getAuthProvider();

  if (provider === 'keycloak') {
    return await getKeycloakAuthUrl();
  }

  if (provider === '3d-only-query') {
    throw new Error('NO_TOKEN_QUERY');
  }

  // Default to 3D auth
  return `https://flowai.ru/auth/sign-in?redirect=${getRedirectUrl()}/`;
};

const getKeycloakLogoutUrl = (): string => {
  const keycloakUrl = trimTrailingSlashes(env.VITE_KEYCLOAK_URL);
  const realm = env.VITE_KEYCLOAK_REALM;
  const clientId = env.VITE_KEYCLOAK_CLIENT_ID;
  const postLogoutRedirectUri = getCurrentAppUrl();

  return `${keycloakUrl}/realms/${realm}/protocol/openid-connect/logout?client_id=${encodeURIComponent(clientId)}&post_logout_redirect_uri=${encodeURIComponent(postLogoutRedirectUri)}`;
};

export const getLogoutUrl = async (): Promise<string> => {
  const provider = getAuthProvider();

  if (provider === 'keycloak') {
    return getKeycloakLogoutUrl();
  }

  if (provider === '3d-only-query') {
    return getCurrentAppUrl();
  }

  return await getAuthUrl();
};

// Export static authUrl for backward compatibility (now async)
export const authUrl = async (): Promise<string> => await getAuthUrl();

// Обработка ошибок ответа
api.instance.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const status = error.response?.status;

    // Обработка 401 ошибки
    if (status === 401) {
      const backendErrorCode = getBackendErrorCode(error.response?.data);

      removeLocalStorageItem('token');

      if (backendErrorCode === AUTH_BLOCK_CODE_DEACTIVATED) {
        setAuthBlockState({
          code: AUTH_BLOCK_CODE_DEACTIVATED,
          message: getBackendErrorMessage(error.response?.data),
        });
        return Promise.reject(error);
      }

      clearAuthBlockState();

      if (getAuthProvider() === '3d-only-query') {
        return Promise.reject(error);
      }

      if (isRedirectingToAuth) {
        return Promise.reject(error);
      }
      isRedirectingToAuth = true;

      try {
        const currentAuthUrl = await getAuthUrl();
        window.location.href = currentAuthUrl;
      } catch (authUrlError) {
        console.error('Ошибка при получении URL аутентификации:', authUrlError);
        isRedirectingToAuth = false;
      }

      return Promise.reject(error);
    }

    // Обработка других ошибок (например, редирект при 403)
    // if (status === 403) {
    //   window.location.href = '#/*';
    // }

    return Promise.reject(error);
  },
);

// api.instance.interceptors.response.use(
//     (response) => response,
//     async (error) => {
//       const originalRequest = error.config;
//
//       if (
//           error.response?.status === 401 &&
//           !originalRequest._retry &&
//           originalRequest.url !== '/token/refresh'
//       ) {
//         originalRequest._retry = true;
//
//         try {
//           const refreshToken = localStorage.getItem('refreshToken');
//           if (!refreshToken) {
//             throw new Error('Нет токена обновления');
//           }
//
//           const response = await axios.post(`${API_URL}/token/refresh`, {
//             refresh_token: refreshToken,
//           });
//
//           const { access_token, refresh_token } = response.data;
//           localStorage.setItem('token', access_token);
//           localStorage.setItem('refreshToken', refresh_token);
//
//           originalRequest.headers.Authorization = `Bearer ${access_token}`;
//           return api.instance(originalRequest);
//         } catch (refreshError) {
//           // Удаляем токены, если произошла ошибка при обновлении
//           localStorage.removeItem('token');
//           localStorage.removeItem('refreshToken');
//           return Promise.reject(refreshError);
//         }
//       }
//
//       // Если ошибка не 401 или если запрос был уже повторен, отклоняем
//       return Promise.reject(error);
//     }
// );

export { api };
